import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { SegmentById, SegmentFilterUIModel } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectBaseQueryConfigurations } from 'store/slices/queryConfigurations';
// Import Hooks
import useToaster from 'hooks/UseToaster';
// Import Components
import SegmentGeneralInfo from './SegmentGeneralInfo';
import StaticSegmentInfo from './StaticSegmentInfo';
import DynamicSegmentInfo from 'components/DynamicSegment/DynamicSegmentInfo';
// Import Services
import { getQueryConfigurationsList } from 'services/segment';
import { getCampaignsBySegment, getStaticSegmentTop200Client } from './service';
// Import Constants
import { SegmentTypesLabels, AlertTypes } from 'constants/common';
// Import Context
import { useVersionHistoryContext } from 'components/VersionHistory';

const { error } = AlertTypes;

interface CompositeSegmentInfoTabProps {
    childSegment: Pick<SegmentById, 'SegmentId' | 'Name' | 'Description' | 'Type' | 'TargetObject'>;
}

const CompositeSegmentInfoTab: React.FC<CompositeSegmentInfoTabProps> = ({ childSegment }) => {
    const {
        Name: name,
        Description: description,
        Type: type,
        SegmentId: segmentId,
        TargetObject: targetObject,
    } = childSegment;

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const queryConfigurations = useAppSelector(selectBaseQueryConfigurations);
    const { pointInTime } = useVersionHistoryContext();
    const { showToaster } = useToaster();

    const [segmentFiltersData, setSegmentFiltersData] = useState<Nullable<Record<string, SegmentFilterUIModel>>>(null);
    const [usedInCampaignsData, setUsedInCampaignsData] = useState(null);
    const [segmentData, setSegmentData] = useState(null);

    const { getQueryConfigurationsListRequest, getCampaignsBySegmentRequest, getStaticSegmentTop200ClientRequest } =
        useMemo(
            () => ({
                getQueryConfigurationsListRequest: getQueryConfigurationsList(),
                getCampaignsBySegmentRequest: getCampaignsBySegment(),
                getStaticSegmentTop200ClientRequest: getStaticSegmentTop200Client(),
            }),
            [],
        );

    const errorHandler = useCallback((alertMessage) => showToaster(error, alertMessage), [showToaster]);

    const renderSegmentInfo = () => {
        switch (type) {
            case SegmentTypesLabels.Dynamic:
                return <DynamicSegmentInfo segmentFiltersData={segmentFiltersData} />;
            case SegmentTypesLabels.Static:
                return <StaticSegmentInfo staticSegmentInfoData={segmentData} />;
            default:
                return '';
        }
    };

    const init = () => {
        getCampaignsBySegmentRequest.request(segmentId, setUsedInCampaignsData, errorHandler, pointInTime);

        if (type === SegmentTypesLabels.Dynamic) {
            getQueryConfigurationsListRequest.request(
                dispatch,
                queryConfigurations,
                childSegment,
                {
                    shouldNotDispatch: true,
                    setData: setSegmentFiltersData,
                },
                t,
            );
        }

        if (type === SegmentTypesLabels.Static) {
            getStaticSegmentTop200ClientRequest.request(
                segmentId,
                (data) => setSegmentData({ ...data, SegmentId: segmentId, Name: name }),
                errorHandler,
            );
        }
    };

    const cleanUp = () => {
        return () => {
            getCampaignsBySegmentRequest.cancel('CompositeSegmentInfoTab:getCampaignsBySegmentRequest');
            getQueryConfigurationsListRequest.cancel('CompositeSegmentInfoTab:getQueryConfigurationsListRequest');
            getStaticSegmentTop200ClientRequest.cancel('CompositeSegmentInfoTab:getStaticSegmentTop200ClientRequest');
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <>
            <SegmentGeneralInfo
                name={name}
                description={description}
                type={type}
                usedInCampaigns={usedInCampaignsData}
                segmentId={segmentId}
                targetObject={targetObject}
            />
            {renderSegmentInfo()}
        </>
    );
};

export default memo(CompositeSegmentInfoTab);
